<template>
    <v-container fluid>
        <v-row align="center" justify="space-between" class="mb-1">
            <v-col cols="auto">
                <div class="d-flex align-center">
                    <v-avatar color="blue-grey lighten-5" size="48" class="mr-4 rounded-lg">
                        <v-icon color="blue-grey darken-3" size="26">mdi-bulletin-board</v-icon>
                    </v-avatar>
                    
                    <div>
                        <h2 class="text-h5 font-weight-bold blue-grey--text text--darken-4 mb-1">
                            Master Settings Update Notice
                        </h2>
                        <p class="text-body-2 grey--text text--darken-1 mb-0 font-weight-medium">
                            Notify the HRD IHS department in advance about the master settings update
                        </p>
                    </div>
                </div>
            </v-col>
 
            <v-col cols="auto">
                <v-btn 
                    color="primary" 
                    class="text-none font-weight-bold px-5 rounded-lg" 
                    height="44"
                    depressed
                    @click="dialog = true"
                >
                    <v-icon left>mdi-plus</v-icon> New
                </v-btn>
            </v-col>
        </v-row>
 
       <v-divider class="mb-1"></v-divider>
 
        <v-row class="mb-1">
            <v-col cols="12" class="pb-0 pt-0">
                <div class="mb-10">
                    <v-row>
                        <template v-if="loading">
                            <v-col v-for="n in 3" :key="'skeleton-' + n" cols="12" md="4" sm="6">
                                <v-card class="rounded-lg note-card" outlined elevation="0">
                                    <v-skeleton-loader
                                        type="card-heading, article, actions"
                                        class="px-2 py-3"
                                    ></v-skeleton-loader>
                                </v-card>
                            </v-col>
                        </template>

                        <template v-else>
                            <v-col v-for="(item, i) in sortedNotices" :key="i" cols="12" md="4" sm="6">
                                <v-card 
                                    class="rounded-lg note-card" 
                                    elevation="2" 
                                    outlined
                                >
                                    <v-card-title 
                                        :style="{ backgroundColor: item.isReplied ? '#4CAF50' : '#cccccc', color: item.isReplied ? 'white' : '#1976D2' }" 
                                        class="py-3 px-4"
                                    >
                                        <span class="font-weight-bold text-body-1">{{ item.notice_no }}</span>
                                        <sup class="blink-hard" v-show="!item.isReplied">New</sup>
                                        <v-spacer></v-spacer>
        
                                        <v-btn 
                                            icon 
                                            :color="item.isReplied ? 'grey lighten-1' : 'grey darken-1'" 
                                            v-show="($store.state.loggedInUser && $store.state.loggedInUser.name == item.created_by) && !item.isReplied" 
                                            :loading="deletingId === item.id"
                                            @click="deleteNotice(item)"
                                        >
                                            <v-icon color="error">mdi-delete</v-icon>
                                        </v-btn>
                                    </v-card-title>
                    
                                    <v-card-text class="pa-4">
                                        <h3 class="pb-2 text-h6 text--primary font-weight-bold" style="text-decoration: underline;">{{ item.title }}</h3>
                                        <p class="text-body-2 mb-3" style="white-space: pre-wrap;">{{ item.content }}</p>
        
                                        <div class="mt-2 mb-3" v-if="item.attachment">
                                            <v-chip 
                                                v-for="(file, index) in item.attachment.split(',')" 
                                                :key="index"
                                                color="blue-grey lighten-5" 
                                                class="text-none primary--text mr-1 mb-1" 
                                                small 
                                                label 
                                                @click="viewAttachment(item, file.trim())"
                                            >
                                                <v-icon left small>mdi-file-document-outline</v-icon>
                                                {{ file.trim() }}
                                            </v-chip>
                                        </div>
        
                                        <div class="d-flex flex-column gap-1">
                                            <p class="text-caption grey--text text--darken-1 mb-1">
                                                <v-icon small left class="mr-1">mdi-account</v-icon>Sent by: {{ item.created_by }}
                                            </p>
                                            <p class="text-caption grey--text text--darken-1 mb-1">
                                                <v-icon small left class="mr-1">mdi-calendar-export</v-icon>Date sent: {{ item.created_at }}
                                            </p>
                                            <p class="text-caption mb-0 font-weight-bold d-inline-block" style="background-color: #FFFF99; width: fit-content; padding: 2px 6px; border-radius: 4px;">
                                                <v-icon small left color="black" class="mr-1">mdi-clock-alert-outline</v-icon>Target date to modify: {{ item.target_date.split(" ")[0] }}
                                            </p>
                                        </div>
                                    </v-card-text>
        
                                    <v-divider></v-divider>
        
                                    <v-card-actions class="pa-4 grey lighten-5" v-if="item.isReplied || ($store.state.loggedInUser && ($store.state.loggedInUser.id == 2 || $store.state.loggedInUser.id == 261))">
                                        <div v-if="!item.isReplied" class="w-100 d-flex flex-column" style="width: 100%;">
                                            
                                            <div class="d-flex align-start w-100">
                                                <div class="flex-grow-1">
                                                    <v-textarea 
                                                        v-model="item.reply"
                                                        class="text-body-2 bg-white rounded" 
                                                        rows="3" 
                                                        auto-grow 
                                                        outlined 
                                                        dense 
                                                        placeholder="Write your reply..." 
                                                        hide-details
                                                    ></v-textarea>
                                                </div>

                                                <div class="d-flex flex-column align-center justify-start" style="width: 44px;">
                                                    <v-file-input
                                                        hide-input
                                                        prepend-icon="mdi-paperclip"
                                                        v-model="item.reply_attach"
                                                        multiple
                                                        class="mt-0 pt-0 ma-0"
                                                        style="max-width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;"
                                                        accept="image/*, .pdf, .xls, .xlsx, application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                    ></v-file-input>
                                                    
                                                    <v-btn 
                                                        icon 
                                                        color="primary" 
                                                        height="44"
                                                        width="44"
                                                        :disabled="!item.reply || !item.reply.trim()"
                                                        :loading="replyingId === item.id"
                                                        @click="sendReply(item)"
                                                    >
                                                        <v-icon>mdi-send</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>

                                            <div class="mt-2 d-flex flex-wrap" v-if="item.reply_attach && item.reply_attach.length > 0">
                                                <v-chip 
                                                    v-for="(file, index) in (Array.isArray(item.reply_attach) ? item.reply_attach : [item.reply_attach])" 
                                                    :key="index"
                                                    color="blue-grey lighten-5" 
                                                    class="text-none primary--text mr-1 mb-1" 
                                                    small 
                                                    label
                                                    close
                                                    style="max-width: 180px;"
                                                    @click:close="item.reply_attach.splice(index, 1)"
                                                >
                                                    <v-icon left small>mdi-file-document-outline</v-icon>
                                                    <span class="d-inline-block text-truncate" style="max-width: 120px;">
                                                        {{ file.name }}
                                                    </span>
                                                </v-chip>
                                            </div>

                                        </div>

                                        <v-row v-else no-gutters>
                                            <v-col cols="12" class="pb-1">
                                                <span style="background-color: #C8E6C9; font-weight: bold; display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 4px;" class="text-caption mb-0 grey--text text--darken-3">
                                                    <v-icon x-small left color="grey darken-3">mdi-reply-outline</v-icon> 
                                                    REPLY BY: {{ item.replied_by }}
                                                    <span class="mx-2 grey--text text--darken-1">•</span>
                                                    <span class="grey--text text--darken-2 font-weight-regular">{{ item.replied_date }}</span>
                                                </span>
                                            </v-col>
                                            <v-col cols="12" class="pt-1">
                                                <div class="text-body-2 grey--text text--darken-3 reply-box" style="white-space: pre-wrap;">{{ item.reply }}</div>
                                                <div class="mt-2 mb-3" v-if="item.reply_attach">
                                                    <v-chip 
                                                        v-for="(file, index) in item.reply_attach.split(',')" 
                                                        :key="index" 
                                                        color="blue-grey lighten-5" 
                                                        class="text-none primary--text mr-1 mb-1" 
                                                        small 
                                                        label 
                                                        style="max-width: 180px;"
                                                        @click="viewAttachment(item, file.trim())"
                                                    >
                                                        <v-icon left small>mdi-file-document-outline</v-icon>
                                                        <span class="d-inline-block text-truncate" style="max-width: 140px;">
                                                            {{ file.trim() }}
                                                        </span>
                                                    </v-chip>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </template>
                    </v-row>
                </div>
            </v-col>
        </v-row>
 
        <v-dialog v-model="dialog" max-width="650" persistent>
            <v-card>
                <v-card-title class="text-h6 font-weight-bold white--text text--darken-3 px-6 py-4 d-flex align-center border-bottom">
                    <v-icon left color="primary" class="mr-2">mdi-note-plus-outline</v-icon>
                    Create New Notice
                    <v-spacer></v-spacer>
                    <v-btn icon small color="grey darken-1" :disabled="submitting" @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <v-form ref="form" v-model="valid" lazy-validation>
                        <v-row no-gutters>
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.title"
                                label="Subject/Title"
                                outlined
                                dense
                                hide-details="auto"
                                :rules="[v => !!v || 'Title is required']"
                                required
                                class="mb-4"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12">
                            <v-menu
                                v-model="dateMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                max-width="290px"
                                min-width="290px"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.target_date"
                                        label="Target Date to Modify"
                                        hint="*Please set atleast 5 days before modification"
                                        persistent-hint
                                        readonly
                                        outlined
                                        dense
                                        hide-details="auto"
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[v => !!v || 'Target date is required']"
                                        required
                                        prepend-inner-icon="mdi-calendar-blank"
                                        class="mb-4"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="form.target_date"
                                    no-title
                                    color="primary"
                                    @input="dateMenu = false"
                                ></v-date-picker>
                            </v-menu>
                        </v-col>

                        <v-col cols="12">
                            <v-textarea
                                v-model="form.content"
                                label="Details of modification"
                                outlined
                                dense
                                rows="4"
                                auto-grow
                                hide-details="auto"
                                :rules="[v => !!v || 'Content details are required']"
                                required
                                class="mb-4"
                                placeholder="Specify the item to modify(add or delete or change)..."
                            ></v-textarea>
                        </v-col>

                        <v-col cols="12">
                            <v-file-input
                                v-model="form.attachmentFile"
                                label="Upload Attachment (optional)"
                                outlined
                                dense
                                hide-details="auto"
                                prepend-icon=""
                                prepend-inner-icon="mdi-paperclip"
                                show-size
                                class="mb-2"
                                multiple
                                accept="image/*, .pdf, .xls, .xlsx, application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            ></v-file-input>
                        </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="px-6 py-4 grey lighten-5">
                    <v-spacer></v-spacer>
                    <v-btn 
                        color="grey darken-2" 
                        text 
                        class="text-none font-weight-medium px-4"
                        :disabled="submitting"
                        @click="closeDialog"
                    >
                        Cancel
                    </v-btn>
                    <v-btn 
                        color="primary" 
                        class="text-none font-weight-bold px-5 rounded-lg ml-2"
                        depressed
                        :disabled="!valid"
                        :loading="submitting"
                        @click="saveNotice"
                    >
                        <v-icon left size="18">mdi-send</v-icon> Submit Notice
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
   </v-container>
</template>
 
<script>
export default {
    data() {
        return {
            env: {
                response : process.env.MIX_ENV == 'local' ? 'response-local' : 'response',
                request : process.env.MIX_ENV == 'local' ? 'request-local' : 'request',
            },
            dialog: false,
            dateMenu: false,
            valid: true,
            
            loading: true,
            submitting: false,
            replyingId: null,
            deletingId: null,

            form: {
                title: '',
                target_date: this.getDefaultTargetDate(),
                content: '',
                attachmentFile: null
            },
            notices: []
        }
    },

    computed: {
        sortedNotices() {
            return [...this.notices].sort((a, b) => b.id - a.id);
        }
    },

    mounted() {
        this.fetchNotices();
    },

    methods: {
        async fetchNotices() {
            this.loading = true;
            try {
                const response = await axios({
                    method: 'get',
                    url: 'api/getMasterNotice'
                });
                this.notices = response.data;
            } catch (error) {
                console.error("Error fetching notices:", error);
            } finally {
                this.loading = false;
            }
        },

        getDefaultTargetDate() {
            const date = new Date();
            date.setDate(date.getDate() + 5);
            return date.toISOString().split('T')[0];
        },

        viewAttachment(item, filename) {
            window.open(`viewNoticeAttachment/${item.id}/${filename}`, '_blank');
        },

        closeDialog() {
            this.dialog = false;
            this.$refs.form.reset();
            this.form.attachmentFile = null;
            this.$nextTick(() => {
                this.form.target_date = this.getDefaultTargetDate();
            });
        },

        async saveNotice() {
            if (this.$refs.form.validate()) {
                this.submitting = true;
                const targetFormatted = `${this.form.target_date} 00:00:00`;
                const formdata = new FormData();
                
                formdata.append('created_by', this.$store.state.loggedInUser ? this.$store.state.loggedInUser.name : '-');
                formdata.append('title', this.form.title);
                
                const formattedContent = this.form.content.replace(/\r?\n/g, "\n");
                formdata.append('content', formattedContent);
                
                formdata.append('target_date', targetFormatted);
                formdata.append('isReplied', 0);

                if (this.form.attachmentFile) {
                    const filesArray = Array.isArray(this.form.attachmentFile) 
                        ? this.form.attachmentFile 
                        : [this.form.attachmentFile];

                    filesArray.forEach((file) => {
                        formdata.append('attachments[]', file);
                    });
                }

                try {
                    const response = await axios({
                        method: 'post',
                        url: 'api/saveMasterNotice',
                        data: formdata,
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });

					await this.sendNoticeLocal(response.data.data)
                    this.closeDialog();
                    await this.fetchNotices(); 
                } catch (error) {
                    console.error('Request failed:', error.response ? error.response.data : error.message);
                } finally {
                    this.submitting = false;
                }
            }
        },

        async sendNoticeLocal(data){
            console.log(data,'test')
            const noticeBy = this.$store.state.loggedInUser.name;
            const noticeByEmail = this.$store.state.loggedInUser.email;
            const notice_no = data.notice_no;
            try {
                this.$socket.emit('newUpdate', {
                    dispatchType: this.env.request,
                    data: {
                        data,
                        noticeBy,
                        noticeByEmail,
                        notice_no
                    },
                    functionType: 'sendNoticeLocal'
                });
            } catch (error) {
                console.log(error, 'socket error')
            }
        },
        
        async sendReply(item) {
            this.replyingId = item.id;
            const formdata = new FormData();
                
            const formattedContent = item.reply.replace(/\r?\n/g, "\n");
            formdata.append('reply', formattedContent);
            formdata.append('id', item.id);
            formdata.append('notice_no', item.notice_no);
            formdata.append('replied_by', this.$store.state.loggedInUser ? this.$store.state.loggedInUser.name : '-');

            if (item.reply_attach) {
                const filesArray = Array.isArray(item.reply_attach) 
                    ? item.reply_attach
                    : [item.reply_attach];

                filesArray.forEach((file) => {
                    formdata.append('reply_attach[]', file);
                });
            }

            try {
                const response = await axios({
                    method: 'post',
                    url: 'api/sendReply',
                    data: formdata,
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                await this.fetchNotices(); 
            } catch (error) {
                console.error('Request failed:', error.response ? error.response.data : error.message);
            } finally {
                this.replyingId = null;
            }
        },

        async deleteNotice(item) {
            this.deletingId = item.id;
            try {
                await axios({
                    method : 'post',
                    url : 'api/deleteNotice',
                    data : item
                });
                await this.fetchNotices();
            } catch (error) {
                console.error('Request failed:', error.response ? error.response.data : error.message);
            } finally {
                this.deletingId = null;
            }
        }
    },
}
</script>

<style scoped>
.note-card {
   background-color: #ffffff !important;
   border: 1px solid #e2e8f0 !important;
   transition: all 0.25s ease;
   display: flex;
   flex-direction: column;
   justify-content: space-between;
}

.note-card:hover {
   box-shadow: 0 6px 16px rgba(0,0,0,0.08) !important;
   transform: translateY(-2px);
}

.reply-box {
   background-color: #ffffff;
   border-left: 3px solid #4CAF50;
   padding: 10px 14px;
   border-radius: 0 6px 6px 0;
   box-shadow: inset 0 1px 3px rgba(0,0,0,0.02);
}

.bg-white {
   background-color: #ffffff !important;
}
.gap-1 {
   gap: 4px;
}

.blink-hard {
   animation: blinker 1s step-end infinite;
   padding-left: 10px;
   color: red;
   font-style: italic;
}
@keyframes blinker {
   50% {
      opacity: 0;
   }
}
</style>